import { useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { PaymentRequestStats } from './types';
import { SEND_STATUS_LABELS } from './types';

interface Props {
  refreshKey: number;
  onFilterPac?: () => void;
  onFilterLups?: () => void;
  onFilterSendFailed?: () => void;
}

const formatCLP = (n: number) => n.toLocaleString('es-CL');

interface MetricCardProps {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'neutral' | 'warn' | 'good' | 'bad' | 'pac' | 'lups';
  onClick?: () => void;
}

function MetricCard({ label, value, hint, tone = 'neutral', onClick }: MetricCardProps) {
  const toneClasses = {
    neutral: 'text-slate-900 dark:text-slate-100',
    warn: 'text-amber-700 dark:text-amber-400',
    good: 'text-emerald-700 dark:text-emerald-400',
    bad: 'text-rose-700 dark:text-rose-400',
    pac: 'text-amber-700 dark:text-amber-400',
    lups: 'text-blue-700 dark:text-blue-400',
  } as const;

  const Container = onClick ? 'button' : 'div';

  return (
    <Container
      onClick={onClick}
      className={`text-left rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${onClick ? 'cursor-pointer transition-all hover:border-slate-300 hover:shadow-md' : ''}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-2 font-mono text-3xl font-semibold tabular-nums ${toneClasses[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </Container>
  );
}

export default function PaymentRequestsStats({ refreshKey, onFilterPac, onFilterLups, onFilterSendFailed }: Props) {
  const [stats, setStats] = useState<PaymentRequestStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const url = getAdminEndpointUrl('paymentRequests', 'stats');
        const data = await adminHttpClient.get<PaymentRequestStats>(url);
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error('Error cargando stats payment requests', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-2xl border border-slate-200 bg-white animate-pulse dark:border-slate-800 dark:bg-slate-900" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Cobrando vía PAC"
          value={formatCLP(stats.routingActive.pac)}
          hint="LPRs con pacActive=true (BCI directo)"
          tone="pac"
          onClick={onFilterPac}
        />
        <MetricCard
          label="Cobrando vía Lups"
          value={formatCLP(stats.routingActive.lups)}
          hint="LPRs activos sin PAC (Lups)"
          tone="lups"
          onClick={onFilterLups}
        />
        <MetricCard
          label="Cobrados 24h"
          value={formatCLP(stats.collected24h)}
          hint={`${formatCLP(stats.abandoned24h)} abandonados`}
          tone="good"
        />
        <MetricCard
          label="Envío fallido"
          value={formatCLP(stats.sendFailed)}
          hint="Requieren atención"
          tone={stats.sendFailed > 0 ? 'bad' : 'neutral'}
          onClick={stats.sendFailed > 0 ? onFilterSendFailed : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Distribución por sendStatus</p>
          <div className="space-y-2">
            {stats.bySendStatus.length === 0 && <p className="text-xs text-slate-400">Sin payment requests</p>}
            {stats.bySendStatus.map((row) => (
              <div key={row.sendStatus} className="flex items-center justify-between">
                <span className="text-xs text-slate-600 dark:text-slate-400">{SEND_STATUS_LABELS[row.sendStatus] ?? row.sendStatus}</span>
                <span className="font-mono text-xs font-semibold tabular-nums text-slate-800 dark:text-slate-200">{formatCLP(row.count)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Otros indicadores</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 dark:text-slate-400">Abandono próximo (≤ 7d)</span>
              <span className={`font-mono text-xs font-semibold tabular-nums ${stats.abandonmentsThisWeek > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>{formatCLP(stats.abandonmentsThisWeek)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
